"use client";

import React, { useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { 
  FaArrowLeft, 
  FaMotorcycle, 
  FaClock, 
  FaCheckCircle, 
  FaTimes, 
  FaStore,
  FaMapMarkerAlt,
  FaReceipt,
  FaPhoneAlt,
  FaRegCopy
} from 'react-icons/fa';
import Sidebar from '../../components/layout/Sidebar';
import MobileNavigation from '../../components/layout/MobileNavigation';
import MobileHeader from '../../components/layout/MobileHeader';

// Mock data for a single order
const MOCK_ORDERS = {
  'OM1234': {
    id: 'OM1234',
    restaurant: {
      name: 'Burger King',
      image: '🍔',
      address: 'Shop #5, Food Street, Block 13-D, Gulshan-e-Iqbal, Karachi',
      phone: '+92 300 1234567'
    },
    customer: {
      name: 'Muhammad Ali',
      address: 'House #10, Street 5, Model Town, Lahore',
      phone: '+92 333 9876543'
    },
    items: [
      { name: 'Whopper', quantity: 2, price: 850, notes: 'Extra cheese' },
      { name: 'French Fries', quantity: 1, price: 350, notes: '' },
      { name: 'Coca Cola', quantity: 2, price: 150, notes: 'No ice' }
    ],
    subtotal: 2200,
    deliveryFee: 100,
    platformFee: 50,
    total: 2350,
    status: 'delivered',
    statusHistory: [
      { status: 'order_placed', time: '2024-03-15T18:00:00', message: 'Order placed' },
      { status: 'payment_confirmed', time: '2024-03-15T18:02:00', message: 'Payment confirmed' },
      { status: 'preparing', time: '2024-03-15T18:05:00', message: 'Restaurant is preparing your order' },
      { status: 'ready_for_pickup', time: '2024-03-15T18:20:00', message: 'Order is ready for pickup' },
      { status: 'picked_up', time: '2024-03-15T18:25:00', message: 'Rider has picked up your order' },
      { status: 'delivered', time: '2024-03-15T18:35:00', message: 'Order delivered' }
    ],
    date: '2024-03-15T18:30:00',
    deliveryTime: '35 min',
    paymentMethod: 'card',
    deliveryMethod: 'standard',
    deliveryInstructions: 'Please ring the doorbell twice',
    lastUpdated: '2024-03-15T18:35:00',
    rider: {
      name: 'Ahmed Khan',
      phone: '+92 321 1234567',
      rating: 4.8
    }
  },
  'OM2345': {
    id: 'OM2345',
    restaurant: {
      name: 'Pizza Hut',
      image: '🍕',
      address: '27 Main Boulevard, Gulberg III, Lahore',
      phone: '+92 300 7654321'
    },
    customer: {
      name: 'Muhammad Ali',
      address: 'House #10, Street 5, Model Town, Lahore',
      phone: '+92 333 9876543'
    },
    items: [
      { name: 'Super Supreme', quantity: 1, price: 1400, notes: 'Extra cheese, thin crust' },
      { name: 'Garlic Bread', quantity: 1, price: 450, notes: '' }
    ],
    subtotal: 1850,
    deliveryFee: 150,
    platformFee: 50,
    total: 2050,
    status: 'active',
    statusHistory: [
      { status: 'order_placed', time: '2024-03-16T14:00:00', message: 'Order placed' },
      { status: 'payment_confirmed', time: '2024-03-16T14:02:00', message: 'Payment confirmed' },
      { status: 'preparing', time: '2024-03-16T14:05:00', message: 'Restaurant is preparing your order' },
      { status: 'ready_for_pickup', time: '2024-03-16T14:15:00', message: 'Order is ready for pickup' },
      { status: 'picked_up', time: '2024-03-16T14:20:00', message: 'Rider has picked up your order' }
    ],
    date: '2024-03-16T14:20:00',
    deliveryTime: '25 min',
    estimatedDelivery: '14:50',
    paymentMethod: 'googlepay',
    deliveryMethod: 'express',
    deliveryInstructions: 'Call when reaching the gate',
    lastUpdated: '2024-03-16T14:20:00',
    rider: {
      name: 'Bilal Ahmad',
      phone: '+92 321 7654321',
      rating: 4.7
    }
  },
  'OM3456': {
    id: 'OM3456',
    restaurant: {
      name: 'KFC',
      image: '🍗',
      address: 'Shop #12, F-7 Markaz, Jinnah Super, Islamabad',
      phone: '+92 300 2468135'
    },
    customer: {
      name: 'Muhammad Ali',
      address: 'House #10, Street 5, Model Town, Lahore',
      phone: '+92 333 9876543'
    },
    items: [
      { name: 'Zinger Burger', quantity: 1, price: 650, notes: 'Extra spicy' },
      { name: 'Hot Wings (6pcs)', quantity: 1, price: 500, notes: '' },
      { name: 'Pepsi', quantity: 1, price: 150, notes: '' }
    ],
    subtotal: 1300,
    deliveryFee: 100,
    platformFee: 50,
    total: 1450,
    status: 'cancelled',
    statusHistory: [
      { status: 'order_placed', time: '2024-03-14T20:00:00', message: 'Order placed' },
      { status: 'payment_confirmed', time: '2024-03-14T20:02:00', message: 'Payment confirmed' },
      { status: 'preparing', time: '2024-03-14T20:05:00', message: 'Restaurant is preparing your order' },
      { status: 'cancelled', time: '2024-03-14T20:15:00', message: 'Order cancelled' }
    ],
    date: '2024-03-14T20:15:00',
    cancellationReason: 'Restaurant was too busy',
    paymentMethod: 'cod',
    deliveryMethod: 'standard',
    deliveryInstructions: '',
    lastUpdated: '2024-03-14T20:15:00'
  }
};

// Add more mock orders as needed...

export default function OrderDetailPage() {
  const params = useParams();
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Simulate API call to fetch order details
    setTimeout(() => {
      const mockOrder = MOCK_ORDERS[params.id];
      if (mockOrder) {
        setOrder(mockOrder);
      }
      setLoading(false);
    }, 500); // Simulate a delay
  }, [params.id]);
  
  // Function to format date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Function to format time
  const formatTime = (dateString) => {
    const options = { 
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleTimeString('en-US', options);
  };
  
  // Function to get status badge styling
  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return {
          color: 'bg-blue-100 text-blue-700',
          bgColor: 'bg-blue-50',
          icon: <FaMotorcycle className="mr-1" />,
          label: 'On the way'
        };
      case 'preparing':
        return {
          color: 'bg-yellow-100 text-yellow-700',
          bgColor: 'bg-yellow-50',
          icon: <FaClock className="mr-1" />,
          label: 'Preparing'
        };
      case 'delivered':
        return {
          color: 'bg-green-100 text-green-700',
          bgColor: 'bg-green-50',
          icon: <FaCheckCircle className="mr-1" />,
          label: 'Delivered'
        };
      case 'cancelled':
        return {
          color: 'bg-red-100 text-red-700',
          bgColor: 'bg-red-50',
          icon: <FaTimes className="mr-1" />,
          label: 'Cancelled'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-700',
          bgColor: 'bg-gray-50',
          icon: null,
          label: status.charAt(0).toUpperCase() + status.slice(1)
        };
    }
  };
  
  // Get the status timeline item style
  const getTimelineItemStyle = (status) => {
    switch(status) {
      case 'order_placed':
      case 'payment_confirmed':
      case 'preparing':
      case 'ready_for_pickup':
      case 'picked_up':
        return 'bg-blue-500';
      case 'delivered':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  // Handle copy to clipboard
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert(`Copied: ${text}`);
  };
  
  // Function to render order tracking
  const renderOrderTracking = () => {
    if (!order || !order.statusHistory) return null;
    
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <h2 className="text-lg font-bold mb-4">Order Tracking</h2>
        
        <div className="relative">
          {order.statusHistory.map((item, index) => {
            const isLast = index === order.statusHistory.length - 1;
            const isFirst = index === 0;
            
            return (
              <div className="flex mb-6" key={item.status}>
                <div className="relative flex flex-col items-center mr-4">
                  <div className={`w-5 h-5 rounded-full ${getTimelineItemStyle(item.status)} z-10 flex items-center justify-center`}>
                    {index === order.statusHistory.length - 1 && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  {!isLast && (
                    <div className={`h-full w-0.5 ${getTimelineItemStyle(item.status)} absolute top-5`}></div>
                  )}
                </div>
                
                <div className="flex-1">
                  <p className="font-medium">{item.message}</p>
                  <p className="text-sm text-gray-500">{formatTime(item.time)}</p>
                </div>
              </div>
            );
          })}
          
          {/* Future status updates if active order */}
          {order.status === 'active' && (
            <>
              <div className="flex mb-6">
                <div className="relative flex flex-col items-center mr-4">
                  <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-300 z-10"></div>
                  <div className="h-full w-0.5 bg-gray-200 absolute top-5"></div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-400">Order delivered</p>
                  <p className="text-sm text-gray-400">Estimated: {order.estimatedDelivery}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };
  
  // Render active rider info
  const renderRiderInfo = () => {
    if (!order || !order.rider || !['active', 'preparing'].includes(order.status)) return null;
    
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <h2 className="text-lg font-bold mb-4">Delivery Information</h2>
        
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
            <FaMotorcycle size={20} className="text-gray-500" />
          </div>
          <div>
            <p className="font-medium">{order.rider.name}</p>
            <p className="text-sm text-gray-500">Your delivery partner</p>
          </div>
          <div className="ml-auto">
            <a 
              href={`tel:${order.rider.phone}`} 
              className="bg-primary text-white py-2 px-4 rounded-full flex items-center text-sm"
            >
              <FaPhoneAlt className="mr-2" /> Call
            </a>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Estimated delivery time: <span className="font-medium">{order.deliveryTime}</span>
          </p>
          <div className="flex items-center">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg 
                  key={star} 
                  className={`w-4 h-4 ${star <= Math.floor(order.rider.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
              ))}
            </div>
            <span className="text-sm ml-1">{order.rider.rating}</span>
          </div>
        </div>
      </div>
    );
  };
  
  // Render loading state
  if (loading) {
    return (
      <main className="flex min-h-screen">
        <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <MobileHeader setMobileOpen={setMobileOpen} />
        
        <div className="flex-1 md:ml-64 h-screen overflow-y-auto bg-gray-50">
          <div className="p-4 md:p-6 mt-14 md:mt-0">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center mb-6">
                <button 
                  onClick={() => router.back()} 
                  className="text-gray-500 hover:text-primary mr-3"
                >
                  <FaArrowLeft />
                </button>
                <div className="h-6 bg-gray-200 rounded-md w-32 animate-pulse"></div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="h-6 bg-gray-200 rounded-md w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded-md w-1/2 mb-6 animate-pulse"></div>
                
                <div className="space-y-4">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded-md w-1/3 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded-md w-1/4 animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="h-6 bg-gray-200 rounded-md w-1/4 mb-4 animate-pulse"></div>
                <div className="space-y-6">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="flex">
                      <div className="w-5 h-5 rounded-full bg-gray-200 mr-4 animate-pulse"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded-md w-3/4 mb-2 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded-md w-1/4 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <MobileNavigation />
      </main>
    );
  }
  
  // Render not found state
  if (!order) {
    return (
      <main className="flex min-h-screen">
        <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <MobileHeader setMobileOpen={setMobileOpen} />
        
        <div className="flex-1 md:ml-64 h-screen overflow-y-auto bg-gray-50">
          <div className="p-4 md:p-6 mt-14 md:mt-0">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center mb-6">
                <button 
                  onClick={() => router.back()} 
                  className="text-gray-500 hover:text-primary mr-3"
                >
                  <FaArrowLeft />
                </button>
                <h1 className="text-2xl font-bold">Order Details</h1>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="text-5xl mb-4">🔍</div>
                <h2 className="text-xl font-medium mb-2">Order Not Found</h2>
                <p className="text-gray-500 mb-6">
                  We couldn't find the order you're looking for. It might have been removed or expired.
                </p>
                <Link href="/orders" className="bg-primary text-white py-3 px-6 rounded-md inline-block">
                  Back to Orders
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        <MobileNavigation />
      </main>
    );
  }
  
  const statusBadge = getStatusBadge(order.status);
  
  return (
    <main className="flex min-h-screen">
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <MobileHeader setMobileOpen={setMobileOpen} />
      
      <div className="flex-1 md:ml-64 h-screen overflow-y-auto bg-gray-50">
        <div className="p-4 md:p-6 mt-14 md:mt-0">
          <div className="max-w-4xl mx-auto mb-20">
            {/* Header with back button */}
            <div className="flex items-center mb-6">
              <button 
                onClick={() => router.back()} 
                className="text-gray-500 hover:text-primary mr-3"
              >
                <FaArrowLeft />
              </button>
              <h1 className="text-2xl font-bold">Order Details</h1>
            </div>
            
            {/* Order header */}
            <div className={`bg-white rounded-lg shadow-sm p-4 mb-6 border-l-4 ${statusBadge.color.replace('bg-', 'border-').replace('text-', '')}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center mb-1">
                    <h2 className="text-xl font-bold mr-2">Order #{order.id}</h2>
                    <div className={`flex items-center px-3 py-1 rounded-full text-sm ${statusBadge.color}`}>
                      {statusBadge.icon}
                      <span>{statusBadge.label}</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">Placed on {formatDate(order.date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="font-bold text-lg">Rs. {order.total}</p>
                </div>
              </div>
              
              {/* Order actions for active orders */}
              {order.status === 'active' && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex gap-2 justify-end">
                    <button className="bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-md text-sm">
                      Support
                    </button>
                    {/* Only show cancel button if in early stages */}
                    {order.statusHistory.length <= 2 && (
                      <button className="bg-red-50 border border-red-100 text-red-600 py-2 px-4 rounded-md text-sm">
                        Cancel Order
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Order tracking */}
            {renderOrderTracking()}
            
            {/* Rider info for active orders */}
            {renderRiderInfo()}
            
            {/* Restaurant Information */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <h2 className="text-lg font-bold mb-3">Restaurant Information</h2>
              <div className="flex items-center">
                <div className="bg-primary text-white w-12 h-12 rounded-full flex items-center justify-center text-xl mr-3 flex-shrink-0">
                  {order.restaurant.image}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">{order.restaurant.name}</h3>
                  <div className="flex items-center text-sm text-gray-500">
                    <FaMapMarkerAlt className="mr-1 text-gray-400" />
                    <p className="truncate">{order.restaurant.address}</p>
                  </div>
                </div>
                <a
                  href={`tel:${order.restaurant.phone}`}
                  className="bg-gray-100 text-gray-700 py-2 px-4 rounded-full text-sm flex items-center ml-2"
                >
                  <FaPhoneAlt className="mr-2" /> Call
                </a>
              </div>
            </div>
            
            {/* Order Details */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <h2 className="text-lg font-bold mb-4">Order Details</h2>
              
              <div className="border-b border-gray-100 pb-4 mb-4">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center">
                        <span className="font-medium w-5 text-center">{item.quantity}×</span>
                        <span className="ml-2">{item.name}</span>
                      </div>
                      {item.notes && (
                        <p className="text-sm text-gray-500 ml-7">Note: {item.notes}</p>
                      )}
                    </div>
                    <p className="font-medium">Rs. {item.price * item.quantity}</p>
                  </div>
                ))}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span>Rs. {order.subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Delivery Fee ({order.deliveryMethod === 'express' ? 'Express' : 'Standard'})</span>
                  <span>Rs. {order.deliveryFee}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Platform Fee</span>
                  <span>Rs. {order.platformFee}</span>
                </div>
                <div className="flex justify-between font-bold mt-3 pt-3 border-t border-gray-100">
                  <span>Total</span>
                  <span>Rs. {order.total}</span>
                </div>
              </div>
            </div>
            
            {/* Delivery Information */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <h2 className="text-lg font-bold mb-4">Delivery Information</h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Delivery Address</p>
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-gray-400 mt-1 mr-2" />
                    <p>{order.customer.address}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Contact Information</p>
                  <div className="flex items-center">
                    <FaPhoneAlt className="text-gray-400 mr-2" />
                    <p>{order.customer.phone}</p>
                    <button 
                      onClick={() => handleCopy(order.customer.phone)}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <FaRegCopy />
                    </button>
                  </div>
                </div>
                
                {order.deliveryInstructions && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Delivery Instructions</p>
                    <p className="italic text-gray-700">{order.deliveryInstructions}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Payment Information */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <h2 className="text-lg font-bold mb-4">Payment Information</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Payment Method</p>
                    <p>
                      {order.paymentMethod === 'cod' && 'Cash On Delivery'}
                      {order.paymentMethod === 'card' && 'Credit/Debit Card'}
                      {order.paymentMethod === 'easypaisa' && 'Easypaisa'}
                      {order.paymentMethod === 'jazzcash' && 'JazzCash'}
                      {order.paymentMethod === 'googlepay' && 'Google Pay'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-1">Receipt</p>
                    <button className="text-primary flex items-center">
                      <FaReceipt className="mr-1" /> View Receipt
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button 
                onClick={() => router.back()} 
                className="bg-white border border-gray-300 text-gray-700 py-3 px-6 rounded-md flex-1 sm:flex-initial text-center"
              >
                Back to Orders
              </button>
              <button className="bg-primary text-white py-3 px-6 rounded-md flex-1 sm:flex-initial">
                Get Help
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <MobileNavigation />
    </main>
  );
} 